import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SERVICE_CATEGORIES, URGENCIES } from "@/lib/constants";
import { Loader2, PlusCircle, X } from "lucide-react";
import { toast } from "sonner";

export default function TenantRequestForm() {
  const navigate = useNavigate();
  const [f, setF] = useState({ service_category: "", problem_description: "", urgency: "Routine", access_instructions: "" });
  const [windows, setWindows] = useState([]);
  const [win, setWin] = useState("");
  const [busy, setBusy] = useState(false);

  const addWindow = () => { if (win.trim()) { setWindows([...windows, win.trim()]); setWin(""); } };

  const submit = async () => {
    if (!f.service_category || !f.problem_description) return toast.error("Category and description are required");
    setBusy(true);
    try {
      const { data } = await api.post("/tenant/work-orders", { ...f, preferred_windows: windows });
      toast.success(`Request ${data.wo_number} submitted`);
      navigate(`/app/tenant/requests/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit"); }
    setBusy(false);
  };

  return (
    <DashboardLayout nav={getNav("tenant")} title="New Maintenance Request">
      <div className="max-w-2xl bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        <div>
          <Label>Issue Category</Label>
          <Select value={f.service_category} onValueChange={(v) => setF({ ...f, service_category: v })}>
            <SelectTrigger data-testid="tenant-category" className="mt-1.5"><SelectValue placeholder="Select a category" /></SelectTrigger>
            <SelectContent className="max-h-56">{SERVICE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Describe the Problem</Label>
          <Textarea data-testid="tenant-description" value={f.problem_description} onChange={(e) => setF({ ...f, problem_description: e.target.value })} className="mt-1.5" rows={4} placeholder="e.g. Water leaking under the kitchen sink since this morning." />
        </div>
        <div>
          <Label>Urgency</Label>
          <Select value={f.urgency} onValueChange={(v) => setF({ ...f, urgency: v })}>
            <SelectTrigger data-testid="tenant-urgency" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>{URGENCIES.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label>Access Instructions</Label>
          <Textarea data-testid="tenant-access" value={f.access_instructions} onChange={(e) => setF({ ...f, access_instructions: e.target.value })} className="mt-1.5" rows={2} placeholder="e.g. Please call before arriving. Dog in the yard." />
        </div>
        <div>
          <Label>Preferred Scheduling Windows</Label>
          <div className="flex gap-2 mt-1.5">
            <Input data-testid="tenant-window-input" value={win} onChange={(e) => setWin(e.target.value)} placeholder="e.g. Weekday mornings" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addWindow())} />
            <Button type="button" variant="outline" onClick={addWindow}>Add</Button>
          </div>
          {windows.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {windows.map((w, i) => <span key={i} className="text-xs bg-slate-100 text-slate-600 rounded-full px-3 py-1 flex items-center gap-1">{w}<button onClick={() => setWindows(windows.filter((_, j) => j !== i))}><X className="w-3 h-3" /></button></span>)}
            </div>
          )}
        </div>
        <p className="text-xs text-slate-400">Photos/videos can be added on the request page after submitting.</p>
        <Button data-testid="tenant-submit-btn" onClick={submit} disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PlusCircle className="w-4 h-4 mr-1" /> Submit Request</>}</Button>
      </div>
    </DashboardLayout>
  );
}
